import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  PortalModuleDetail, usePortal, portalDetailPath, humanizePortalError,
  createAction, createNote, createReceipt, attachModule,
  PORTAL_REGISTRY_ROOT,
  type ActionPriority,
} from './olivium/portal-factory'

/**
 * Portal module route wrapper.
 *
 *   /square/command-center/olivium/portals/:portalId/modules/:moduleId
 *
 * Owns usePortal for the parent portal and scopes work to :moduleId. Core
 * modules persist module-scoped actions / notes / receipts; inherited modules
 * deep-link into the existing V2 spine. Reloads after each mutation.
 */
export default function SquareCodePortalModuleRoute() {
  const navigate = useNavigate()
  const { portalId, moduleId } = useParams<{ portalId: string; moduleId: string }>()
  const state = usePortal(portalId)
  const id = portalId || ''
  const moduleKey = moduleId || ''
  const [mutationError, setMutationError] = useState<string | null>(null)

  const after = (p: Promise<unknown>): Promise<boolean> =>
    p.then(
      () => { setMutationError(null); return state.reload().then(() => true) },
      (e) => { setMutationError(humanizePortalError(e)); return state.reload().then(() => false) },
    )

  return (
    <PortalModuleDetail
      detail={state.detail}
      moduleKey={moduleKey}
      loading={state.loading}
      error={state.error}
      authRequired={state.authRequired}
      notFound={state.notFound}
      mutationError={mutationError}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onOpenPortal={() => navigate(portalDetailPath(id))}
      onOpenRegistry={() => navigate(PORTAL_REGISTRY_ROOT)}
      onOpenLink={(url) => navigate(url)}
      onAttachModule={(key: string) => after(attachModule(id, key))}
      onCreateAction={(input: { title: string; priority: ActionPriority; module_key: string }) => after(createAction(id, input))}
      onCreateNote={(body: string, module_key: string) => after(createNote(id, body, module_key))}
      onCreateReceipt={(summary: string, module_key: string) => after(createReceipt(id, summary, module_key))}
    />
  )
}
